import axios, { AxiosResponse } from 'axios'
import { useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'
import { validateFormatTelephone } from '../../util/validate'

export type Response = { result?: AxiosResponse<any>; error?: any }

export interface Form {
    coverImage?: any[]
    organizationName?: string
    address?: string
    telephone?: string
    callCenter?: string
    email?: string
    website?: string
    line?: string
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.organizationName) {
        errors.organizationName = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.address) {
        errors.address = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.telephone) {
        errors.telephone = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.telephone && !validateFormatTelephone(values.telephone)) {
        errors.telephone = `${t('VALIDATE.MESSAGES.TELEPHONE')}`
    }
    if (!values.callCenter) {
        errors.callCenter = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.email && !isEmail(values.email)) {
        errors.email = `${t('VALIDATE.MESSAGES.EMAIL')}`
    }
    if (values.website && !isURL(values.website)) {
        errors.website = `${t('VALIDATE.MESSAGES.URL')}`
    }
    return errors
}

const useContact = () => {
    const valueSubmitted = useRef<Form>()
    const [initialData, setInitialData] = useState<AboutUsType>({})
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const [actionType, setActionType] = useState<string>('')
    const { t } = useTranslation()
    const history = useHistory()
    const loadingStore = useLoadingStore()

    const callServiceGetInitial = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/aboutus`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setStatusCode(200)
                setInitialData(result.data)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const aboutUsId = initialData?.aboutUsId
            const url = `${process.env.REACT_APP_API}/api/content/manage/aboutus/${aboutUsId}`
            const reqParams = {
                organizationName: data.organizationName,
                contactNumber: data.telephone,
                address: data.address,
                contactEmail: data.email,
                callCenter: data.callCenter,
                lineId: data.line,
                website: data.website,
            }
            const result = await axios.put(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))

                if (data.coverImage && data.coverImage?.length > 0) {
                    manageFile(data.coverImage, 'COVER')
                } else if (initialData.coverImage?.fileId) {
                    callServiceAttachmentDelete(initialData.coverImage?.fileId)
                }
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAttachmentAdd = async (formData: any) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/attachment/cms/contactus`
            const result = await axios.post(url, formData, ConfigAPI)
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceAttachmentDelete = async (fileId: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/attachment/cms/${fileId}`
            const result = await axios.delete(url, ConfigAPI)
            return { result }
        } catch (error) {
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const uploadFile = async (files: any[], fileType: AttachmentRequestType) => {
        try {
            let uploadFile = await Promise.all(
                files.map(async (file: any, index: number) => {
                    const formData = new FormData()
                    formData.append('file', file)
                    formData.append('fileType', fileType)

                    const { result } = await callServiceAttachmentAdd(formData)
                    return result
                }),
            )

            return uploadFile
        } catch (error) {
            return { error }
        }
    }

    const manageFile = (files: any[], fileType: AttachmentRequestType) => {
        const duplicateFile = files.filter((file) => file.fileId === initialData.coverImage?.fileId)
        if (duplicateFile.length === 0) {
            if (initialData.coverImage?.fileId) {
                callServiceAttachmentDelete(initialData.coverImage?.fileId)
            }
            uploadFile(files, fileType)
        } else if (files.length > 0) {
            uploadFile(files, fileType)
        }
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        setActionType('save')
        setOpenDialogSave(true)
    }

    const handleClickBack = (dirty: boolean) => {
        // ตอนกดปุ่มยกเลิกที่ form
        if (dirty) {
            setOpenDialogBack(true)
        } else {
            setOpenDialogBack(false)
            history.push('/backoffice/contact-management')
        }
        setActionType('back')
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/backoffice/contact-management')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = () => {
        callServiceEdit(valueSubmitted.current as Form)
        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 401) {
            history.push('/login')
        } else if (!isDuplicate) {
            callServiceGetInitial()
            history.push('/backoffice/contact-management')
        }
        setIsDuplicate(false)
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickCancelBack: handleClickCancelBack,
        onClickCancelSave: handleClickCancelSave,
        onClickConfirmBack: handleClickConfirmBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetInitial,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        actionType,
    }
}

export default useContact
